import {
  WBBox,
  WBCollapse,
  WBFlex,
  WBIcon,
  WBLink,
  WBTypography,
  WBView,
} from '@admiin-com/ds-web';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { DocumentAnalysisLineItemInput, TaxType } from '@admiin-com/ds-graphql';
import { InputLabel, styled, useMediaQuery, useTheme } from '@mui/material';
import { BillLineItems } from '../../../BillLineItems';
import { LineItemsMobile } from '../../Scanning/Mobile/LineItemsMobile';
import { useTaskCreationContext } from '../../../../../pages/TaskCreation';
import { getDefaultGST } from '../../../../../helpers/tasks';
import { getAmountFromLineItems } from '../../../../../helpers/lineitems';
import { Bill } from '../../../../PayBill/PayBillCreateForm';

export const SignPayLineItems = ({
  scannedTax,
}: {
  scannedTax: Bill | null;
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  const { taskDirection, taskType } = useTaskCreationContext();

  const from = watch(`futureTaskInput.from`);
  const isTax = React.useMemo(
    () => (from as any)?.metadata?.subCategory === 'TAX',
    [from]
  );

  const isTaxType = React.useMemo(() => taskType === 'tax', [taskType]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const inputs = React.useMemo(
    () => ({
      lineItems: {
        name: 'futureTaskInput.lineItems',
        defaultValue: [],
      },
      amount: {
        name: 'futureTaskInput.amount',
        defaultValue: 0,
      },
    }),
    []
  );
  React.useEffect(() => {
    if (isTax) {
      const lineItems = watch('futureTaskInput.lineItems');
      const newLineItems =
        lineItems?.map((item: any) => {
          if (item.taxType === TaxType.GST_INCLUSIVE) {
            return {
              ...item,
              taxType: 'NO_GST',
            };
          }
          return item;
        }) ?? [];
      setValue('futureTaskInput.lineItems', newLineItems);
    }
  }, [isTax]);

  const updateLineItems = (lineItems: DocumentAnalysisLineItemInput[]) => {
    setValue('futureTaskInput.lineItems', lineItems);
    setValue('futureTaskInput.amount', getAmountFromLineItems(lineItems), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const defaultGST = React.useMemo(() => getDefaultGST(from), [from]);

  const [showLineItems, setShowLineItems] = React.useState(
    isTaxType ? false : true
  );
  const onViewHideLineItems = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLineItems(!showLineItems);
  };

  return (
    <WBBox>
      <WBBox sx={{ my: 2 }}>
        <WBLink onClick={onViewHideLineItems} variant="body2">
          <WBFlex alignItems="center" gap={1}>
            {showLineItems
              ? t('collapseLineItems', { ns: 'tasks' })
              : t('showLineItems', { ns: 'tasks' })}
            <WBTypography
              sx={{
                transform: showLineItems ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
              component={'span'}
              color={'primary.main'}
            >
              <WBIcon name="ChevronDown" size={2} color={'primary.main'} />
            </WBTypography>
          </WBFlex>
        </WBLink>
      </WBBox>
      {/* Hidden amount controller with validation and inline error */}
      <Controller
        name={inputs.amount.name}
        control={control}
        defaultValue={inputs.amount.defaultValue}
        rules={{
          validate: (value) =>
            Number(value) > 0 ||
            t('amountMustBeGreaterThanZero', {
              ns: 'tasks',
              defaultValue: 'Amount must be greater than 0',
            }),
        }}
        render={({ field, fieldState }) => (
          <>
            <input type="hidden" {...field} />
            {fieldState.error && (
              <WBTypography variant="body2" color={'error.main'}>
                {fieldState.error.message}
              </WBTypography>
            )}
          </>
        )}
      />
      <WBCollapse in={showLineItems}>
        <Controller
          name={inputs.lineItems.name}
          control={control}
          defaultValue={inputs.lineItems.defaultValue}
          render={({ field }) => {
            return (
              <WBBox mt={[2, 0]}>
                <InputLabel>{t('lineItems', { ns: 'taskbox' })}</InputLabel>
                {!isMobile && (
                  <LineItemsContainer>
                    <BillLineItems
                      defaultGST={defaultGST}
                      lineItems={field.value}
                      setLineItems={updateLineItems}
                      hasServiceLookup={taskDirection === 'SENDING'}
                      fromEntityId={from?.id}
                    />
                  </LineItemsContainer>
                )}
                {isMobile && (
                  <LineItemsMobile
                    lineItems={field.value}
                    changeLineItems={updateLineItems}
                    index={0}
                    opened={0}
                    setOpened={() => {
                      console.log('setOpened');
                    }}
                  />
                )}
              </WBBox>
            );
          }}
        />
      </WBCollapse>
    </WBBox>
  );
};

const LineItemsContainer = styled(WBView)(({ theme }) => ({}));
